import React, { useState } from 'react';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

// State-wise Professional Tax rates
const PT_RATES = {
  maharashtra: {
    name: 'Maharashtra',
    slabs: [
      { min: 0, max: 7500, tax: 0, period: 'month' },
      { min: 7501, max: 10000, tax: 175, period: 'month' },
      { min: 10001, max: Infinity, tax: 200, period: 'month', note: '₹300 for Feb' },
    ],
    maxAnnual: 2500,
  },
  karnataka: {
    name: 'Karnataka',
    slabs: [
      { min: 0, max: 15000, tax: 0, period: 'month' },
      { min: 15001, max: 25000, tax: 200, period: 'month' },
      { min: 25001, max: Infinity, tax: 200, period: 'month' },
    ],
    maxAnnual: 2400,
  },
  west_bengal: {
    name: 'West Bengal',
    slabs: [
      { min: 0, max: 10000, tax: 0, period: 'month' },
      { min: 10001, max: 15000, tax: 110, period: 'month' },
      { min: 15001, max: 25000, tax: 130, period: 'month' },
      { min: 25001, max: 40000, tax: 150, period: 'month' },
      { min: 40001, max: Infinity, tax: 200, period: 'month' },
    ],
    maxAnnual: 2400,
  },
  tamil_nadu: {
    name: 'Tamil Nadu',
    slabs: [
      { min: 0, max: 21000, tax: 0, period: 'half-year' },
      { min: 21001, max: 30000, tax: 135, period: 'half-year' },
      { min: 30001, max: 45000, tax: 315, period: 'half-year' },
      { min: 45001, max: 60000, tax: 690, period: 'half-year' },
      { min: 60001, max: 75000, tax: 1025, period: 'half-year' },
      { min: 75001, max: Infinity, tax: 1250, period: 'half-year' },
    ],
    maxAnnual: 2500,
  },
  andhra_pradesh: {
    name: 'Andhra Pradesh',
    slabs: [
      { min: 0, max: 15000, tax: 0, period: 'month' },
      { min: 15001, max: 20000, tax: 150, period: 'month' },
      { min: 20001, max: Infinity, tax: 200, period: 'month' },
    ],
    maxAnnual: 2500,
  },
  telangana: {
    name: 'Telangana',
    slabs: [
      { min: 0, max: 15000, tax: 0, period: 'month' },
      { min: 15001, max: 20000, tax: 150, period: 'month' },
      { min: 20001, max: Infinity, tax: 200, period: 'month' },
    ],
    maxAnnual: 2500,
  },
  gujarat: {
    name: 'Gujarat',
    slabs: [
      { min: 0, max: 5999, tax: 0, period: 'month' },
      { min: 6000, max: 8999, tax: 80, period: 'month' },
      { min: 9000, max: 11999, tax: 150, period: 'month' },
      { min: 12000, max: Infinity, tax: 200, period: 'month' },
    ],
    maxAnnual: 2500,
  },
  madhya_pradesh: {
    name: 'Madhya Pradesh',
    slabs: [
      { min: 0, max: 18750, tax: 0, period: 'month' },
      { min: 18751, max: 25000, tax: 125, period: 'month' },
      { min: 25001, max: Infinity, tax: 208, period: 'month' },
    ],
    maxAnnual: 2500,
  },
  kerala: {
    name: 'Kerala',
    slabs: [
      { min: 0, max: 11999, tax: 0, period: 'month' },
      { min: 12000, max: 17999, tax: 120, period: 'month' },
      { min: 18000, max: 29999, tax: 180, period: 'month' },
      { min: 30000, max: Infinity, tax: 250, period: 'month' },
    ],
    maxAnnual: 2500,
  },
  odisha: {
    name: 'Odisha',
    slabs: [
      { min: 0, max: 13304, tax: 0, period: 'month' },
      { min: 13305, max: 25000, tax: 125, period: 'month' },
      { min: 25001, max: Infinity, tax: 200, period: 'month' },
    ],
    maxAnnual: 2500,
  },
};

const ProfessionalTaxCalculator = () => {
  const [state, setState] = useState('maharashtra');
  const [monthlySalary, setMonthlySalary] = useState(50000);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const salary = Number(monthlySalary) || 0;
    const stateData = PT_RATES[state];
    const slabs = stateData.slabs;
    
    let monthlyTax = 0;
    let applicableSlab = null;
    
    for (const slab of slabs) {
      if (salary >= slab.min && salary <= slab.max) {
        monthlyTax = slab.tax;
        applicableSlab = slab;
        break;
      }
    }
    
    // Calculate annual tax
    let annualTax = monthlyTax * 12;
    
    // For Maharashtra, February has ₹300
    if (state === 'maharashtra' && salary > 10000) {
      annualTax = (monthlyTax * 11) + 300;
    }
    
    // Cap at maximum annual
    annualTax = Math.min(annualTax, stateData.maxAnnual);

    setResult({
      stateName: stateData.name,
      monthlySalary: salary,
      monthlyTax,
      annualTax,
      maxAnnual: stateData.maxAnnual,
      applicableSlab,
      slabs,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Professional Tax Calculation', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['State', result.stateName],
        ['Monthly Salary', formatCurrencyForPDF(result.monthlySalary)],
        ['Monthly Professional Tax', formatCurrencyForPDF(result.monthlyTax)],
        ['Annual Professional Tax', formatCurrencyForPDF(result.annualTax)],
        ['Max Annual Limit', formatCurrencyForPDF(result.maxAnnual)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Professional_Tax.pdf');
  };

  return (
    <div className="calculator-page">
      <SEO 
        title="Professional Tax Calculator"
        description="Free Professional Tax Calculator India - Calculate state-wise professional tax on salary."
        keywords="professional tax calculator, PT calculator India, state-wise professional tax, professional tax slab, salary professional tax"
        canonicalPath="/professional-tax"
      />
      <div className="calculator-header">
        <h1>💼 Professional Tax Calculator</h1>
        <p>Calculate state-wise Professional Tax deduction from salary</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Select State</label>
            <select value={state} onChange={(e) => setState(e.target.value)}>
              {Object.entries(PT_RATES).map(([key, value]) => (
                <option key={key} value={key}>{value.name}</option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Monthly Gross Salary (₹)</label>
            <input
              type="number"
              value={monthlySalary === '' ? '' : monthlySalary}
              onChange={(e) => setMonthlySalary(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Professional Tax
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                <h3>Monthly Professional Tax</h3>
                <div className="amount">{formatCurrency(result.monthlyTax)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Annual: {formatCurrency(result.annualTax)}
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">State</span>
                  <span className="value">{result.stateName}</span>
                </div>
                <div className="result-item">
                  <span className="label">Monthly Salary</span>
                  <span className="value">{formatCurrency(result.monthlySalary)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">Monthly PT</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.monthlyTax)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">Annual PT</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.annualTax)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Max Annual Limit</span>
                  <span className="value">{formatCurrency(result.maxAnnual)}</span>
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '16px' }}>
                <h4>{result.stateName} PT Slabs</h4>
                <table className="schedule-table" style={{ marginTop: '8px' }}>
                  <thead>
                    <tr>
                      <th>Salary Range</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.slabs.map((slab, idx) => (
                      <tr key={idx} style={slab === result.applicableSlab ? { background: '#dbeafe' } : {}}>
                        <td>₹{slab.min.toLocaleString()} - {slab.max === Infinity ? 'Above' : '₹' + slab.max.toLocaleString()}</td>
                        <td>₹{slab.tax}/{slab.period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Select state and enter salary to calculate PT</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 About Professional Tax</h4>
        <ul>
          <li>Professional Tax is a state-level tax on income earned from profession/employment</li>
          <li>Maximum limit is ₹2,500 per year in all states</li>
          <li>It's deductible under Section 16 of Income Tax Act</li>
          <li>Some states like Delhi, UP, Rajasthan don't levy Professional Tax</li>
        </ul>
      </div>
    </div>
  );
};

export default ProfessionalTaxCalculator;
